/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// ProductOptionMetadata allows you to attach additional data to each option. It is a dictionary in
// which keys are string and values can be of arbitrary type.
export interface ProductOptionMetadata {
  // This is used by Product page (more specifically, the Palette feature which renders panel for
  // choosing variant). If set to true, user is prompted to select a value rather than using the one
  // corresponding to the navagating variant.
  readonly promptSelection?: boolean;
}

// ProductOptionValueMetadata allows you to attach additional data to each value in an option. It is
// a dictionary in which keys are string and values can be of arbitrary type.
export interface ProductOptionValueMetadata {
  // Botoshop stores "swatch" images in option value's metadata. The value is an image id which
  // reference to one of the product's images.
  readonly swatchImage?: string;
}

export interface ProductOptionData {
  readonly id: string;
  readonly name: string;
  readonly valueMetadata?: { [key: string]: ProductOptionValueMetadata };
  readonly metadata?: ProductOptionMetadata;
}

export interface ProductOptionValue {
  readonly value: string;
  readonly metadata?: ProductOptionValueMetadata;
}

export class ProductOption {
  public readonly values: ProductOptionValue[];

  constructor(
    private readonly data: ProductOptionData,
    valueList: string[],
  ) {
    this.values = valueList.map((value) => ({
      value,
      metadata: (data.valueMetadata) ? data.valueMetadata[value] : undefined,
    }));
  }

  get id() { return this.data.id; }
  get name() { return this.data.name; }
  get shouldPromptSelection() {
    if (this.data.metadata) {
      return Boolean(this.data.metadata.promptSelection);
    }
    return false;
  }
}
